import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import WorkOrderLinkSelect from '../WorkOrders/WorkOrderLinkSelect';
import LabourAdvanceHistory from './LabourAdvanceHistory';
import SettleLabourWalletModal from './SettleLabourWalletModal';
import { LABOUR_SUB_NAV } from '../Expenses/expenseGroupTheme';
import { filterActiveEmployees } from '../../utils/employeeStatusUtils';
import { ChevronDown, HardHat, List, Plus, Wallet } from 'lucide-react';
import AppSelect from '../shared/AppSelect';

const field =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/30';
const fieldSelect =
  `${field} text-left min-h-[44px] flex items-center justify-between`;
const label = 'mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide';

const PERSON_TYPES = ['Employee', 'Labour', 'Freelancer', 'Other'];
const PAYMENT_TYPES = ['Advance', 'Festival Bonus', 'Bonus', 'Incentive', 'Other'];
const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Card', 'Other'];

export default function LabourAdvancesTab(props) {
  const {
    companyCurrency,
    companyCtx,
    db,
    employees,
    irregularForm,
    setIrregularForm,
    handleIrregularFormChange,
    handleSaveIrregular,
    handleIrregularEmployeeSelect,
    showIrregularNameDropdown,
    setShowIrregularNameDropdown,
    filteredIrregularNames,
    handleIrregularNameSelect,
    irregularNameDropdownRef,
    recordInEmployeeCashFlowLabour,
    setRecordInEmployeeCashFlowLabour,
    selectedEmployeeForCashFlowLabour,
    setSelectedEmployeeForCashFlowLabour,
    markLabourSettledFromWallet,
    setMarkLabourSettledFromWallet,
    labourSourceEmployeeBalance,
    rows,
    labourAdvanceFilter,
    setLabourAdvanceFilter,
    labourAdvanceSort,
    setLabourAdvanceSort,
    expandedCards,
    toggleCard,
    handleDeleteIrregular,
    handleOpenSettleLabourFromWallet,
    settleLabourModalPayment,
    setSettleLabourModalPayment,
    settleLabourDate,
    setSettleLabourDate,
    settleLabourPaymentMode,
    setSettleLabourPaymentMode,
    settleLabourEmployeeId,
    setSettleLabourEmployeeId,
    settleLabourEmployeeBalance,
    settleLabourRemark,
    setSettleLabourRemark,
    settleLabourSubmitting,
    handleConfirmSettleLabourFromWallet,
  } = props;

  const [view, setView] = useState('entry');
  const activeEmployees = filterActiveEmployees(employees);
  const employeeOptions = activeEmployees.map((e) => ({
    value: e.employeeId,
    label: `${e.name} (${e.employeeId})`,
  }));

  const setPersonType = (type) => {
    handleIrregularFormChange({ target: { name: 'personType', value: type } });
  };

  const walletPreview =
    selectedEmployeeForCashFlowLabour && labourSourceEmployeeBalance && irregularForm.amount
      ? (() => {
          const amountNum = Number(irregularForm.amount) || 0;
          const paymentMode = (irregularForm.paymentMode || 'Cash').toLowerCase();
          const useCash = paymentMode === 'cash';
          const useUpi = paymentMode === 'upi';
          const available = useCash
            ? labourSourceEmployeeBalance.cash || 0
            : useUpi
              ? labourSourceEmployeeBalance.upi || 0
              : labourSourceEmployeeBalance.total || 0;
          const sufficient = available >= amountNum;
          const selectedEmp = employees?.find((emp) => emp.id === selectedEmployeeForCashFlowLabour);
          return { amountNum, useCash, useUpi, available, sufficient, selectedEmp };
        })()
      : null;

  const entryCount = rows?.length ?? 0;
  const nav = LABOUR_SUB_NAV;

  return (
    <div className="mb-6 space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
        <div className={`bg-gradient-to-r ${nav.header} px-5 py-5 text-white`}>
          <div className="flex items-start gap-3">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl backdrop-blur-sm ${nav.iconTile}`}
            >
              <HardHat className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Labour & advances</h2>
              <p className="text-slate-300 text-sm mt-0.5">
                Record advances, bonuses, and labour payouts
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={nav.wrap}>
        <button
          type="button"
          onClick={() => setView('entry')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'entry' ? nav.active : nav.idle
          }`}
        >
          <Plus className="h-4 w-4" />
          New entry
        </button>
        <button
          type="button"
          onClick={() => setView('history')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'history' ? nav.active : nav.idle
          }`}
        >
          <List className="h-4 w-4" />
          All entries
          {entryCount > 0 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                view === 'history' ? nav.badgeActive : nav.badgeIdle
              }`}
            >
              {entryCount}
            </span>
          )}
        </button>
      </div>

      {view === 'entry' && (
        <form
          onSubmit={handleSaveIrregular}
          className="bg-white rounded-xl border border-gray-200 shadow-lg ring-1 ring-black/5 overflow-hidden"
        >
          <div className={`h-1 bg-gradient-to-r ${nav.topStripe}`} aria-hidden />
          <div className="p-5 sm:p-6 space-y-6">
            <section>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="h-6 w-1 rounded-full bg-teal-600" />
                Who & when
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={label}>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={irregularForm.date}
                    onChange={handleIrregularFormChange}
                    className={field}
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Person type</label>
                  <div className="flex flex-wrap gap-2">
                    {PERSON_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setPersonType(type)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          irregularForm.personType === type
                            ? 'bg-teal-700 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-900'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className={label}>
                    {irregularForm.personType === 'Employee' ? 'Employee' : 'Person name'}
                  </label>
                  {irregularForm.personType === 'Employee' ? (
                    <AppSelect
                      value={irregularForm.employeeId}
                      onChange={handleIrregularEmployeeSelect}
                      placeholder="Select employee…"
                      aria-label="Employee"
                      sheetTitle="Select employee"
                      options={[
                        { value: '', label: 'Select employee…' },
                        ...employeeOptions,
                      ]}
                      triggerClassName={fieldSelect}
                    />
                  ) : (
                    <div className="relative" ref={irregularNameDropdownRef}>
                      <input
                        name="personName"
                        placeholder="Enter person name"
                        value={irregularForm.personName}
                        onChange={handleIrregularFormChange}
                        onFocus={() => {
                          if (irregularForm.personName) setShowIrregularNameDropdown(true);
                        }}
                        className={field}
                      />
                      {showIrregularNameDropdown && filteredIrregularNames.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-auto ring-1 ring-black/5">
                          {filteredIrregularNames.map((name, idx) => (
                            <button
                              key={`${name}-${idx}`}
                              type="button"
                              onClick={() => handleIrregularNameSelect(name)}
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-teal-50 font-medium text-gray-800"
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="h-6 w-1 rounded-full bg-teal-600" />
                Payment details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={label}>Payment type</label>
                  <AppSelect
                    name="paymentType"
                    value={irregularForm.paymentType}
                    onChange={handleIrregularFormChange}
                    aria-label="Payment type"
                    sheetTitle="Payment type"
                    options={PAYMENT_TYPES.map((t) => ({ value: t, label: t }))}
                    triggerClassName={fieldSelect}
                  />
                </div>
                <div>
                  <label className={label}>Amount</label>
                  <input
                    name="amount"
                    type="number"
                    placeholder="0.00"
                    value={irregularForm.amount}
                    onChange={handleIrregularFormChange}
                    className={field}
                    required
                  />
                </div>
                <div>
                  <label className={label}>Payment mode</label>
                  <AppSelect
                    name="paymentMode"
                    value={irregularForm.paymentMode}
                    onChange={handleIrregularFormChange}
                    aria-label="Payment mode"
                    sheetTitle="Payment mode"
                    options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))}
                    triggerClassName={fieldSelect}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Remark</label>
                  <input
                    name="remark"
                    placeholder="Optional note"
                    value={irregularForm.remark}
                    onChange={handleIrregularFormChange}
                    className={field}
                  />
                </div>
                <div>
                  <label className={label}>Settlement remark</label>
                  <input
                    name="settlementRemark"
                    placeholder="Optional"
                    value={irregularForm.settlementRemark}
                    onChange={handleIrregularFormChange}
                    className={field}
                  />
                </div>
              </div>
            </section>

            <details className="group rounded-xl border border-teal-200/80 bg-slate-50/60 open:bg-teal-50/40">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-slate-800">
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Employee wallet settlement (optional)
                </span>
                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="border-t border-teal-200/80/80 px-4 pb-4 pt-3 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordInEmployeeCashFlowLabour}
                    onChange={(e) => {
                      setRecordInEmployeeCashFlowLabour(e.target.checked);
                      if (e.target.checked) {
                        setMarkLabourSettledFromWallet(true);
                      } else {
                        setSelectedEmployeeForCashFlowLabour('');
                        setMarkLabourSettledFromWallet(false);
                      }
                    }}
                    className="mt-1 rounded border-gray-300 text-teal-700 focus:ring-teal-600"
                  />
                  <span className="text-sm text-gray-700">
                    Also record in employee cash flow (deduct from wallet)
                  </span>
                </label>
                {recordInEmployeeCashFlowLabour && (
                  <div className="rounded-lg border border-teal-200/80 bg-white p-4 space-y-3">
                    <div>
                      <label className={label}>Wallet employee *</label>
                      <AppSelect
                        value={selectedEmployeeForCashFlowLabour}
                        onChange={(e) => setSelectedEmployeeForCashFlowLabour(e.target.value)}
                        placeholder="Select employee"
                        aria-label="Wallet employee"
                        sheetTitle="Wallet employee"
                        options={[
                          { value: '', label: 'Select employee' },
                          ...activeEmployees.map((employee) => {
                            const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                            return {
                              value: employee.id,
                              label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                            };
                          }),
                        ]}
                        triggerClassName={fieldSelect}
                      />
                    </div>
                    {walletPreview && (
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-gray-700 space-y-1">
                        <p className="font-semibold text-gray-800">
                          {walletPreview.selectedEmp?.name} ({walletPreview.selectedEmp?.employeeId || 'N/A'})
                        </p>
                        <p>
                          Available: ₹{(labourSourceEmployeeBalance.total || 0).toLocaleString('en-IN')} (Cash: ₹
                          {(labourSourceEmployeeBalance.cash || 0).toLocaleString('en-IN')} | UPI: ₹
                          {(labourSourceEmployeeBalance.upi || 0).toLocaleString('en-IN')})
                        </p>
                        {!walletPreview.sufficient && (
                          <p className="text-red-600 font-medium">
                            Insufficient balance for ₹{walletPreview.amountNum.toLocaleString('en-IN')} (
                            {walletPreview.useCash ? 'Cash' : walletPreview.useUpi ? 'UPI' : 'Total'})
                          </p>
                        )}
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={markLabourSettledFromWallet}
                            onChange={(e) => setMarkLabourSettledFromWallet(e.target.checked)}
                            className="rounded border-gray-300 text-teal-700 focus:ring-teal-600"
                          />
                          <span className="text-sm">Mark this entry as settled from wallet</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </details>

            <WorkOrderLinkSelect
              db={db}
              companyCtx={companyCtx}
              workOrderId={irregularForm.workOrderId}
              phaseId={irregularForm.phaseId}
              costCategory={irregularForm.costCategory}
              onChange={(v) => setIrregularForm((prev) => ({ ...prev, ...v }))}
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-gray-100">
              {irregularForm.amount ? (
                <p className="text-sm text-gray-600">
                  Amount to record:{' '}
                  <span className="text-lg font-bold text-teal-700 tabular-nums">
                    {formatCurrency(Number(irregularForm.amount) || 0, companyCurrency)}
                  </span>
                </p>
              ) : (
                <span />
              )}
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-teal-700 to-teal-800 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:from-teal-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
              >
                <Plus className="h-4 w-4" />
                Add labour / advance entry
              </button>
            </div>
          </div>
        </form>
      )}

      {view === 'history' && (
        <LabourAdvanceHistory
              rows={rows}
              employees={employees}
              companyCurrency={companyCurrency}
              labourAdvanceFilter={labourAdvanceFilter}
              setLabourAdvanceFilter={setLabourAdvanceFilter}
              labourAdvanceSort={labourAdvanceSort}
              setLabourAdvanceSort={setLabourAdvanceSort}
              onDelete={handleDeleteIrregular}
              onSettleFromWallet={handleOpenSettleLabourFromWallet}
              expandedCards={expandedCards}
              toggleCard={toggleCard}
        />
      )}

      <SettleLabourWalletModal
        payment={settleLabourModalPayment}
        employees={employees}
        companyCurrency={companyCurrency}
        settleDate={settleLabourDate}
        setSettleDate={setSettleLabourDate}
        settlePaymentMode={settleLabourPaymentMode}
        setSettlePaymentMode={setSettleLabourPaymentMode}
        settleEmployeeId={settleLabourEmployeeId}
        setSettleEmployeeId={setSettleLabourEmployeeId}
        settleEmployeeBalance={settleLabourEmployeeBalance}
        settleRemark={settleLabourRemark}
        setSettleRemark={setSettleLabourRemark}
        submitting={settleLabourSubmitting}
        onClose={() => setSettleLabourModalPayment(null)}
        onSubmit={handleConfirmSettleLabourFromWallet}
      />
    </div>
  );
}
